import React, { useState } from "react";
import LayersIcon from '@mui/icons-material/Layers';
import WineBarIcon from '@mui/icons-material/WineBar';
import SettingsIcon from '@mui/icons-material/Settings';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import CancelIcon from '@mui/icons-material/Cancel';

const DestilacionSection = ({
  destilacionRecords,
  moliendaRecords,
  isFormOpen,
  setIsFormOpen,
  formData,
  setFormData,
  handleSaveDestilacion,
  handleCancelDestilacion,
  handleRejectDestilacion
}) => {
  const [selectedRecord, setSelectedRecord] = useState(null);
  const destilacionItems = formData?.items || [];

  // Agregar una nueva fila vacía de 2da destilación
  const handleAddItem = () => {
    const today = new Date().toISOString().split('T')[0];
    const nextNum = destilacionItems.length + 1;
    const newItem = {
      id: Date.now(),
      fechaSegunda: today,
      destiladorNum: `Destilador ${nextNum < 10 ? '0' + nextNum : nextNum}`,
      nuevoLote: "",
      volADestilar: "",
      esDestiladoCon: false,
      destiladoConIngrediente: "",
      puntasVol: "",
      puntasAlc: "",
      mezcalVol: "",
      mezcalAlc: "",
      colasVol: "",
      colasAlc: ""
    };
    setFormData(prev => ({
      ...prev,
      items: [...(prev.items || []), newItem]
    }));
  };

  // Eliminar una fila
  const handleRemoveItem = (id) => {
    if (destilacionItems.length === 1) {
      alert("Debe mantener al menos un registro de 2da destilación.");
      return;
    }
    setFormData(prev => ({
      ...prev,
      items: (prev.items || []).filter(item => item.id !== id)
    }));
  };

  // Actualizar un campo específico de una fila
  const handleItemChange = (id, field, value) => {
    setFormData(prev => ({
      ...prev,
      items: (prev.items || []).map(item => item.id === id ? { ...item, [field]: value } : item)
    }));
  };

  // Manejo de etiquetas múltiples para "Destilado con"
  const handleAddIngredient = (itemId, newIng) => {
    if (!newIng || !newIng.trim()) return;
    const cleanIng = newIng.trim();
    
    setDestilacionItems(prevItems => 
      prevItems.map(item => {
        if (item.id !== itemId) return item;

        const currentList = item.destiladoConIngredientes || (item.destiladoConIngrediente ? item.destiladoConIngrediente.split(', ').filter(Boolean) : []);
        if (currentList.includes(cleanIng)) return item;

        const updatedList = [...currentList, cleanIng];
        return {
          ...item,
          destiladoConIngredientes: updatedList,
          destiladoConIngrediente: updatedList.join(', ')
        };
      })
    );
  };

  const handleRemoveIngredient = (itemId, ingToRemove) => {
    setDestilacionItems(prevItems => 
      prevItems.map(item => {
        if (item.id !== itemId) return item;

        const currentList = item.destiladoConIngredientes || (item.destiladoConIngrediente ? item.destiladoConIngrediente.split(', ').filter(Boolean) : []);
        const updatedList = currentList.filter(ing => ing !== ingToRemove);
        return {
          ...item,
          destiladoConIngredientes: updatedList,
          destiladoConIngrediente: updatedList.join(', ')
        };
      })
    );
  };

  const handleEditRecord = (rec) => {
    if (rec.status !== "borrador" && rec.status !== "rechazado") {
      alert("Solo se pueden editar registros que se encuentren en estado BORRADOR o RECHAZADO.");
      return;
    }
    setFormData({
      noProduccion: rec.noProduccion || "",
      observaciones: rec.observaciones || "",
      items: (rec.items && Array.isArray(rec.items) && rec.items.length > 0) ? rec.items : [
        {
          id: Date.now(),
          fechaSegunda: rec.fechaSegunda || "",
          destiladorNum: rec.destiladorNum || "Destilador 01",
          nuevoLote: rec.nuevoLote || item.nuevoLote || "",
          volADestilar: rec.volADestilar ? String(rec.volADestilar).replace(/[^\d.]/g, '') : "",
          esDestiladoCon: false,
          destiladoConIngrediente: "",
          puntasVol: rec.puntas ? rec.puntas.split(' ')[0] : "",
          puntasAlc: "",
          mezcalVol: rec.mezcal ? rec.mezcal.split(' ')[0] : "",
          mezcalAlc: "",
          colasVol: rec.colas ? rec.colas.split(' ')[0] : "",
          colasAlc: ""
        }
      ]
    });
    setIsFormOpen(true);
  };

  // Validación de balance de volumen: Puntas + Mezcal + Colas <= Vol. a Destilar
  const onSubmitForm = (status) => {
    for (let i = 0; i < destilacionItems.length; i++) {
      const item = destilacionItems[i];
      const volDestilar = parseFloat(item.volADestilar || 0);
      const puntas = parseFloat(item.puntasVol || 0);
      const mezcal = parseFloat(item.mezcalVol || 0);
      const colas = parseFloat(item.colasVol || 0);
      const sumaCortes = puntas + mezcal + colas;

      if (volDestilar > 0 && sumaCortes > volDestilar) {
        alert(
          `¡Atención en Destilación #${i + 1}!\n\n` +
          `La suma de los cortes (Puntas: ${puntas} L + Mezcal: ${mezcal} L + Colas: ${colas} L = ${sumaCortes} L) ` +
          `no puede ser mayor al Volumen a Destilar (${volDestilar} L).\n\n` +
          `Excedente: ${(sumaCortes - volDestilar).toFixed(1)} Litros. Por favor corrija los valores antes de guardar.`
        );
        return;
      }
    }
    handleSaveDestilacion(status);
  };

  if (!isFormOpen) {
    return (
      <div className="animate-fadeIn">
        <div className="history-section-header">
          <h2>Destilación / Rectificación (Sección IV)</h2>
          <button 
            onClick={() => setIsFormOpen(true)}
            className="btn-add-new"
          >
            <AddIcon style={{ fontSize: 18 }} />
            <span>Nuevo Registro de Destilación</span>
          </button>
        </div>

        <div className="records-list-wrapper">
          <table className="records-table">
            <thead>
              <tr>
                <th>No. Producción</th>
                <th>Destilaciones</th>
                <th>Mezcal Obtenido</th>
                <th>Clasificación</th>
                <th>Observaciones</th>
                <th>V.Bo. Inspector</th>
                <th>Estado</th>
                <th style={{ textAlign: "center" }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {destilacionRecords.map((rec, i) => {
                const numItems = rec.items ? rec.items.length : 1;
                const tieneDestiladoCon = rec.items ? rec.items.some(item => item.esDestiladoCon) : false;

                return (
                  <tr key={i}>
                    <td style={{ fontWeight: 800 }}>{rec.noProduccion}</td>
                    <td>
                      <span style={{ background: "rgba(255,255,255,0.06)", padding: "4px 8px", borderRadius: "4px", fontSize: "12px", color: "var(--agave-green)", fontWeight: 700 }}>
                        {numItems} {numItems === 1 ? 'destilación' : 'destilaciones'}
                      </span>
                    </td>
                    <td style={{ color: "var(--agave-green)", fontWeight: 700 }}>
                      {rec.mezcal || "-"}
                    </td>
                    <td>
                      <span style={{
                        padding: "3px 8px",
                        borderRadius: "12px",
                        fontSize: "11px",
                        fontWeight: 600,
                        background: tieneDestiladoCon ? "rgba(230, 126, 34, 0.2)" : "rgba(46, 204, 113, 0.2)",
                        color: tieneDestiladoCon ? "#e67e22" : "#2ecc71",
                        border: tieneDestiladoCon ? "1px solid rgba(230, 126, 34, 0.4)" : "1px solid rgba(46, 204, 113, 0.4)"
                      }}>
                        {tieneDestiladoCon ? "Destilado con" : "Blanco / Joven"}
                      </span>
                    </td>
                    <td style={{ maxWidth: "200px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={rec.observaciones}>
                      {rec.observaciones}
                    </td>
                    <td style={{ fontSize: "11px" }}>{rec.inspector}</td>
                    <td>
                      <span className={`badge-status ${rec.status}`}>
                        {rec.status === "aprobado" ? "Aprobado" : rec.status === "revision" ? "En revisión" : "Borrador"}
                      </span>
                    </td>
                    <td style={{ textAlign: "center" }}>
                      <div style={{ display: "flex", justifyContent: "center", gap: "6px" }}>
                        <button 
                          type="button" 
                          onClick={() => setSelectedRecord(rec)}
                          title="Ver Detalle Completo"
                          style={{ background: "rgba(52, 152, 219, 0.15)", color: "#3498db", border: "1px solid rgba(52, 152, 219, 0.3)", borderRadius: "6px", padding: "5px 8px", cursor: "pointer", display: "flex", alignItems: "center" }}
                        >
                          <VisibilityIcon style={{ fontSize: 16 }} />
                        </button>

                        <button 
                          type="button" 
                          onClick={() => handleEditRecord(rec)}
                          disabled={rec.status !== "borrador" && rec.status !== "rechazado"}
                          title={(rec.status === "borrador" || rec.status === "rechazado") ? "Editar Registro" : "Solo editables en estado Borrador o Rechazado"}
                          style={{ 
                            background: (rec.status === "borrador" || rec.status === "rechazado") ? "rgba(241, 196, 15, 0.15)" : "rgba(255,255,255,0.04)", 
                            color: (rec.status === "borrador" || rec.status === "rechazado") ? "#f1c40f" : "var(--text-muted)", 
                            border: (rec.status === "borrador" || rec.status === "rechazado") ? "1px solid rgba(241, 196, 15, 0.3)" : "1px solid rgba(255,255,255,0.08)", 
                            borderRadius: "6px", 
                            padding: "5px 8px", 
                            cursor: (rec.status === "borrador" || rec.status === "rechazado") ? "pointer" : "not-allowed", 
                            display: "flex", 
                            alignItems: "center",
                            opacity: (rec.status === "borrador" || rec.status === "rechazado") ? 1 : 0.4
                          }}
                        >
                          <EditIcon style={{ fontSize: 16 }} />
                        </button>

                        {rec.status !== "cancelada" && rec.status !== "cancelado" && rec.status !== "aprobado" && handleRejectDestilacion && (
                          <button
                            type="button"
                            onClick={() => handleRejectDestilacion(rec)}
                            title="Rechazar para Corrección"
                            style={{ background: "rgba(245, 158, 11, 0.15)", color: "#f39c12", border: "1px solid rgba(245, 158, 11, 0.3)", borderRadius: "6px", padding: "5px 8px", cursor: "pointer", display: "flex", alignItems: "center" }}
                          >
                            <ReportProblemIcon style={{ fontSize: 16 }} />
                          </button>
                        )}

                        {rec.status !== "cancelada" && rec.status !== "cancelado" && handleCancelDestilacion && (
                          <button
                            type="button"
                            onClick={() => handleCancelDestilacion(rec)}
                            title="Cancelar Bitácora"
                            style={{ background: "rgba(239, 68, 68, 0.15)", color: "#e74c3c", border: "1px solid rgba(239, 68, 68, 0.3)", borderRadius: "6px", padding: "5px 8px", cursor: "pointer", display: "flex", alignItems: "center" }}
                          >
                            <CancelIcon style={{ fontSize: 16 }} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* MODAL DE DETALLE COMPLETO DE DESTILACIÓN */}
        {selectedRecord && (
          <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.75)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 999, padding: "20px" }}>
            <div className="detail-card animate-fadeIn" style={{ maxWidth: "800px", width: "100%", maxHeight: "90vh", overflowY: "auto", background: "#161b22", border: "1px solid rgba(255,255,255,0.15)", borderRadius: "12px", padding: "24px", position: "relative" }}>
              <button 
                onClick={() => setSelectedRecord(null)}
                style={{ position: "absolute", top: "16px", right: "16px", background: "transparent", border: "none", color: "var(--text-muted)", cursor: "pointer" }}
              >
                <CloseIcon />
              </button>

              <div style={{ borderBottom: "1px solid rgba(255,255,255,0.1)", paddingBottom: "12px", marginBottom: "16px" }}>
                <span className={`badge-status ${selectedRecord.status}`} style={{ float: "right", marginTop: "4px" }}>
                  {selectedRecord.status === "aprobado" ? "Aprobado" : selectedRecord.status === "enviado" ? "Enviado" : selectedRecord.status === "revision" ? "En revisión" : selectedRecord.status === "rechazado" ? "Rechazado (Para corrección)" : selectedRecord.status === "cancelada" ? "Cancelada" : "Borrador"}
                </span>
                <h3 style={{ margin: 0, color: "var(--agave-green)", fontSize: "20px" }}>Solicitud: {selectedRecord.noProduccion}</h3>
                <small style={{ color: "var(--text-muted)" }}>Detalle Completo de Segunda Destilación y Cortes de Rectificación (Sección IV)</small>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", fontSize: "14px", marginBottom: "16px" }}>
                <div><strong>Inspector:</strong> {selectedRecord.inspector}</div>
                <div><strong>Total Registros:</strong> {selectedRecord.items ? selectedRecord.items.length : 1} destilaciones</div>
              </div>

              <div style={{ overflowX: "auto", marginBottom: "16px" }}>
                <table className="records-table" style={{ fontSize: "12px" }}>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Fecha 2da Dest.</th>
                      <th>Destilador</th>
                      <th>Nuevo Lote</th>
                      <th>Vol. a Destilar</th>
                      <th>Clasificación</th>
                      <th>Puntas (L / %)</th>
                      <th>Mezcal (Corazón)</th>
                      <th>Colas (L / %)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedRecord.items && selectedRecord.items.map((item, idx) => (
                      <tr key={idx}>
                        <td>{idx + 1}</td>
                        <td>{item.fechaSegunda}</td>
                        <td style={{ fontWeight: 700 }}>{item.destiladorNum}</td>
                        <td style={{ color: "var(--copper)", fontWeight: 700 }}>{item.nuevoLote || item.loteResultante || '-'}</td>
                        <td>{item.volADestilar}</td>
                        <td>
                          <span style={{
                            padding: "2px 6px",
                            borderRadius: "10px",
                            fontSize: "10px",
                            fontWeight: 600,
                            background: item.esDestiladoCon ? "rgba(230, 126, 34, 0.2)" : "rgba(46, 204, 113, 0.2)",
                            color: item.esDestiladoCon ? "#e67e22" : "#2ecc71"
                          }}>
                            {item.esDestiladoCon ? `Destilado con ${item.destiladoConIngrediente || ''}`.trim() : "Blanco / Joven"}
                          </span>
                        </td>
                        <td>{item.puntas || `${item.puntasVol || 0} L (${item.puntasAlc || 0}%)`}</td>
                        <td style={{ color: "var(--agave-green)", fontWeight: 700 }}>
                          {item.mezcal || `${item.mezcalVol || 0} L (${item.mezcalAlc || 0}%)`}
                        </td>
                        <td>{item.colas || `${item.volumenColas || item.colasVol || 0} L (${item.colasAlc || 0}%)`}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div style={{ paddingTop: "12px", borderTop: "1px solid rgba(255,255,255,0.1)" }}>
                <strong>Observaciones Generales:</strong>
                <p style={{ margin: "6px 0 0 0", color: "var(--text-muted)", fontSize: "13px" }}>{selectedRecord.observaciones || "Sin observaciones."}</p>
              </div>

              {(selectedRecord.observaciones_correccion || selectedRecord.observacionesCorreccion) && (
                <div style={{ marginTop: "12px", background: "rgba(245, 158, 11, 0.12)", border: "1px solid rgba(245, 158, 11, 0.3)", borderRadius: "10px", padding: "12px" }}>
                  <span style={{ color: "#D97706", fontSize: "12px", fontWeight: 800, display: "flex", alignItems: "center", gap: "6px" }}>
                    ⚠️ Observaciones de Corrección (Unidad de Inspección UI):
                  </span>
                  <p style={{ margin: "6px 0 0 0", color: "#F59E0B", fontSize: "13px", fontWeight: 600 }}>
                    {selectedRecord.observaciones_correccion || selectedRecord.observacionesCorreccion}
                  </p>
                </div>
              )}

              <div style={{ marginTop: "20px", display: "flex", justifyContent: "flex-end", gap: "10px" }}>
                {(selectedRecord.status === "borrador" || selectedRecord.status === "rechazado") && (
                  <button 
                    onClick={() => {
                      const rec = selectedRecord;
                      setSelectedRecord(null);
                      handleEditRecord(rec);
                    }}
                    className="btn-add-new"
                    style={{ padding: "8px 16px", fontSize: "13px" }}
                  >
                    <EditIcon style={{ fontSize: 16 }} />
                    <span>Editar Registro</span>
                  </button>
                )}
                {selectedRecord.status !== "cancelada" && selectedRecord.status !== "cancelado" && selectedRecord.status !== "aprobado" && handleRejectDestilacion && (
                  <button 
                    onClick={() => {
                      const rec = selectedRecord;
                      setSelectedRecord(null);
                      handleRejectDestilacion(rec);
                    }}
                    style={{ background: "rgba(245, 158, 11, 0.15)", color: "#D97706", border: "1px solid rgba(245, 158, 11, 0.3)", borderRadius: "6px", padding: "8px 16px", cursor: "pointer", display: "flex", alignItems: "center", gap: "6px", fontSize: "13px", fontWeight: 700 }}
                  >
                    <ReportProblemIcon style={{ fontSize: 16 }} />
                    <span>Rechazar</span>
                  </button>
                )}
                {selectedRecord.status !== "cancelada" && selectedRecord.status !== "cancelado" && handleCancelDestilacion && (
                  <button 
                    onClick={() => {
                      const rec = selectedRecord;
                      setSelectedRecord(null);
                      handleCancelDestilacion(rec);
                    }}
                    style={{ background: "rgba(239, 68, 68, 0.15)", color: "#ef4444", border: "1px solid rgba(239, 68, 68, 0.3)", borderRadius: "6px", padding: "8px 16px", cursor: "pointer", display: "flex", alignItems: "center", gap: "6px", fontSize: "13px", fontWeight: 600 }}
                  >
                    <CancelIcon style={{ fontSize: 16 }} />
                    <span>Cancelar</span>
                  </button>
                )}
                <button onClick={() => setSelectedRecord(null)} className="btn-secondary-custom" style={{ padding: "8px 16px" }}>
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ================= FORMULARIO DE CAPTURA (FORM VIEW) =================
  return (
    <div className="animate-fadeIn">
      <div className="history-section-header">
        <button onClick={() => setIsFormOpen(false)} className="btn-secondary-custom" style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 16px" }}>
          <ArrowBackIcon style={{ fontSize: 16 }} />
          <span>Volver al Historial</span>
        </button>
        <h2>Segunda Destilación y Cortes de Rectificación - Sección IV</h2>
      </div>

      {/* SELECCIÓN DE SOLICITUD / LOTE MOLIENDA */}
      <div className="detail-card detail-card-accent-left-green" style={{ marginBottom: "20px" }}>
        <header className="detail-card-header">
          <div className="detail-card-icon-wrapper-green">
            <LayersIcon />
          </div>
          <div className="detail-card-title-wrapper">
            <h2>Identificación de la Solicitud de Producción</h2>
            <p>Seleccione el lote validado proveniente de Molienda y Fermentación</p>
          </div>
        </header>

        <div className="custom-input-group">
          <select 
            value={formData.noProduccion || ""} 
            onChange={e => setFormData(prev => ({ ...prev, noProduccion: e.target.value }))}
            className="custom-select-field"
            style={{ padding: "18px 16px 8px 16px" }}
          >
            <option value="">-- Seleccionar Lote Molienda --</option>
            {moliendaRecords.map((r, idx) => (
              <option key={idx} value={r.noProduccion}>
                {r.noProduccion} ({r.observaciones ? r.observaciones.slice(0, 40) + '...' : 'Molienda Registrada'})
              </option>
            ))}
          </select>
          <label className="custom-input-label">No. de Producción (Validado Molienda)</label>
        </div>
      </div>

      {/* ENCABEZADO DE SECCIÓN DE REGISTROS DE DESTILACIÓN */}
      <div style={{ marginBottom: "16px" }}>
        <h3 style={{ margin: 0, color: "var(--copper)", fontSize: "18px" }}>
          Registros de Destilación / Rectificación ({destilacionItems.length})
        </h3>
      </div>

      {/* LISTADO DE ITEMS DINÁMICOS DE SEGUNDA DESTILACIÓN */}
      {destilacionItems.map((item, index) => (
        <div key={item.id || index} className="detail-card detail-card-accent-left-copper" style={{ marginBottom: "20px", padding: "18px", position: "relative" }}>
          
          {/* ENCABEZADO Y ELIMINAR */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <div className="detail-card-icon-wrapper-copper" style={{ width: "32px", height: "32px" }}>
                <WineBarIcon style={{ fontSize: 18 }} />
              </div>
              <h3 style={{ margin: 0, color: "var(--copper)", fontSize: "16px", fontWeight: 700 }}>
                Destilación # {index + 1}
              </h3>
            </div>

            {destilacionItems.length > 1 && (
              <button 
                type="button" 
                onClick={() => handleRemoveItem(item.id)}
                title="Eliminar este apartado"
                style={{ background: "rgba(231, 76, 60, 0.15)", color: "#e74c3c", border: "1px solid rgba(231, 76, 60, 0.3)", borderRadius: "6px", padding: "4px 10px", cursor: "pointer", display: "flex", alignItems: "center", gap: "4px", fontSize: "12px" }}
              >
                <DeleteOutlineIcon style={{ fontSize: 16 }} />
                <span>Quitar</span>
              </button>
            )}
          </div>

          {/* ================= FILA 1: DATOS OPERATIVOS ================= */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "12px", marginBottom: "16px", background: "rgba(255,255,255,0.02)", padding: "12px", borderRadius: "8px", border: "1px solid rgba(255,255,255,0.06)" }}>
            <div className="custom-input-group">
              <input 
                type="date" 
                value={item.fechaSegunda} 
                onChange={e => handleItemChange(item.id, 'fechaSegunda', e.target.value)} 
                className="custom-input-field"
              />
              <label className="custom-input-label">Fecha de Destilación</label>
            </div>

            <div className="custom-input-group">
              <input 
                type="text" 
                value={item.destiladorNum} 
                onChange={e => handleItemChange(item.id, 'destiladorNum', e.target.value)} 
                className="custom-input-field"
                placeholder="Ej. Destilador Cobre 01"
              />
              <label className="custom-input-label">Número de Destilador / Contenedor</label>
            </div>

            <div className="custom-input-group">
              <input 
                type="text" 
                value={item.nuevoLote || ""} 
                onChange={e => handleItemChange(item.id, 'nuevoLote', e.target.value)} 
                className="custom-input-field"
                placeholder="Ej. LOTE-DEST-01"
              />
              <label className="custom-input-label">Nuevo Lote de Destilación</label>
            </div>

            <div className="custom-input-group">
              <input 
                type="number" 
                value={item.volADestilar} 
                onChange={e => handleItemChange(item.id, 'volADestilar', e.target.value)} 
                className="custom-input-field"
                placeholder="0.00"
              />
              <label className="custom-input-label">Volumen a Destilar (L)</label>
              <span className="input-suffix">L</span>
            </div>

            <div style={{ display: "flex", flexDirection: "column", justifyContent: "center" }}>
              <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer", fontSize: "13px", fontWeight: 600, color: item.esDestiladoCon ? "#e67e22" : "var(--text-color)" }}>
                <input 
                  type="checkbox"
                  checked={item.esDestiladoCon}
                  onChange={e => handleItemChange(item.id, 'esDestiladoCon', e.target.checked)}
                  style={{ width: "18px", height: "18px", accentColor: "#e67e22", cursor: "pointer" }}
                />
                <span>¿Es "Destilado con"?</span>
              </label>

              {item.esDestiladoCon && (
                <div style={{ marginTop: "8px", display: "flex", flexDirection: "column", gap: "6px" }}>
                  
                  {/* ENTRADA DE TEXTO Y BOTÓN AÑADIR */}
                  <div style={{ display: "flex", gap: "6px" }}>
                    <input 
                      type="text" 
                      id={`ing-input-${item.id}`}
                      onKeyDown={e => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddIngredient(item.id, e.target.value);
                          e.target.value = '';
                        }
                      }}
                      className="custom-input-field"
                      style={{ fontSize: "12px", padding: "6px 10px", flex: 1 }}
                      placeholder="Escriba ingrediente y Enter..."
                    />
                    <button 
                      type="button" 
                      onClick={() => {
                        const inputEl = document.getElementById(`ing-input-${item.id}`);
                        if (inputEl) {
                          handleAddIngredient(item.id, inputEl.value);
                          inputEl.value = '';
                        }
                      }}
                      style={{ background: "rgba(230, 126, 34, 0.2)", border: "1px solid rgba(230, 126, 34, 0.5)", color: "#e67e22", borderRadius: "6px", padding: "4px 10px", fontSize: "12px", fontWeight: 700, cursor: "pointer" }}
                    >
                      + Añadir
                    </button>
                  </div>

                  {/* SUGERENCIAS RÁPIDAS (INGREDIENTES TRADICIONALES DE MEZCAL DESTILADO CON) */}
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
                    {[
                      "Pechuga de Pavo", 
                      "Pechuga de Gallina", 
                      "Conejo", 
                      "Manzana Criolla", 
                      "Plátano", 
                      "Tejocote", 
                      "Piña", 
                      "Pasas", 
                      "Almendra", 
                      "Canela", 
                      "Anís Estrella", 
                      "Poleo", 
                      "Cacao", 
                      "Café"
                    ].map((sug, sIdx) => (
                      <button 
                        key={sIdx}
                        type="button"
                        onClick={() => handleAddIngredient(item.id, sug)}
                        style={{ 
                          background: "rgba(230, 126, 34, 0.08)", 
                          border: "1px solid rgba(230, 126, 34, 0.2)", 
                          borderRadius: "10px", 
                          color: "var(--text-muted)", 
                          fontSize: "10px", 
                          padding: "2px 7px", 
                          cursor: "pointer",
                          transition: "all 0.15s ease"
                        }}
                        onMouseEnter={e => {
                          e.target.style.background = "rgba(230, 126, 34, 0.25)";
                          e.target.style.color = "#e67e22";
                        }}
                        onMouseLeave={e => {
                          e.target.style.background = "rgba(230, 126, 34, 0.08)";
                          e.target.style.color = "var(--text-muted)";
                        }}
                      >
                        + {sug}
                      </button>
                    ))}
                  </div>

                  {/* ETIQUETAS / CHIPS AGREGADAS */}
                  {(() => {
                    const ingList = item.destiladoConIngredientes || (item.destiladoConIngrediente ? item.destiladoConIngrediente.split(', ').filter(Boolean) : []);
                    if (ingList.length === 0) return (
                      <span style={{ fontSize: "11px", color: "var(--text-muted)", italic: "true" }}>
                        Sin ingredientes agregados aún.
                      </span>
                    );

                    return (
                      <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginTop: "4px" }}>
                        {ingList.map((ing, idx) => (
                          <span 
                            key={idx}
                            style={{ 
                              background: "rgba(230, 126, 34, 0.2)", 
                              border: "1px solid rgba(230, 126, 34, 0.5)", 
                              color: "#e67e22", 
                              borderRadius: "12px", 
                              padding: "3px 8px", 
                              fontSize: "11px", 
                              fontWeight: 600, 
                              display: "flex", 
                              alignItems: "center", 
                              gap: "4px" 
                            }}
                          >
                            {ing}
                            <button 
                              type="button" 
                              onClick={() => handleRemoveIngredient(item.id, ing)}
                              style={{ background: "none", border: "none", color: "#e67e22", cursor: "pointer", padding: 0, fontSize: "12px", fontWeight: 800, lineHeight: 1 }}
                            >
                              ×
                            </button>
                          </span>
                        ))}
                      </div>
                    );
                  })()}

                </div>
              )}
            </div>
          </div>

          {/* ================= FILA 2: CORTES DE RECTIFICACIÓN ================= */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1.2fr 1fr", gap: "12px", background: "rgba(255,255,255,0.015)", padding: "12px", borderRadius: "8px", border: "1px solid rgba(255,255,255,0.06)" }}>
            
            {/* PUNTAS */}
            <div style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: "8px", padding: "10px" }}>
              <div style={{ fontSize: "11px", fontWeight: 700, color: "var(--text-muted)", marginBottom: "8px", textTransform: "uppercase" }}>
                Puntas
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
                <div className="custom-input-group">
                  <input 
                    type="number" 
                    value={item.puntasVol} 
                    onChange={e => handleItemChange(item.id, 'puntasVol', e.target.value)} 
                    className="custom-input-field" 
                    placeholder="0.00"
                  />
                  <label className="custom-input-label">Volumen (L)</label>
                </div>
                <div className="custom-input-group">
                  <input 
                    type="number" 
                    step="0.1"
                    value={item.puntasAlc} 
                    onChange={e => handleItemChange(item.id, 'puntasAlc', e.target.value)} 
                    className="custom-input-field" 
                    placeholder="0.0"
                  />
                  <label className="custom-input-label">% Alc. Vol.</label>
                </div>
              </div>
            </div>

            {/* MEZCAL (CORAZÓN) */}
            <div style={{ background: "rgba(46, 204, 113, 0.06)", border: "1px solid rgba(46, 204, 113, 0.25)", borderRadius: "8px", padding: "10px" }}>
              <div style={{ fontSize: "11px", fontWeight: 700, color: "var(--agave-green)", marginBottom: "8px", textTransform: "uppercase" }}>
                Mezcal (Corazón)
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
                <div className="custom-input-group">
                  <input 
                    type="number" 
                    value={item.mezcalVol} 
                    onChange={e => handleItemChange(item.id, 'mezcalVol', e.target.value)} 
                    className="custom-input-field" 
                    style={{ fontWeight: 700, color: "var(--agave-green)" }}
                    placeholder="0.00"
                  />
                  <label className="custom-input-label">Volumen (L)</label>
                </div>
                <div className="custom-input-group">
                  <input 
                    type="number" 
                    step="0.1"
                    value={item.mezcalAlc} 
                    onChange={e => handleItemChange(item.id, 'mezcalAlc', e.target.value)} 
                    className="custom-input-field" 
                    style={{ fontWeight: 700 }}
                    placeholder="0.0"
                  />
                  <label className="custom-input-label">% Alc. Vol.</label>
                </div>
              </div>
            </div>

            {/* COLAS */}
            <div style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: "8px", padding: "10px" }}>
              <div style={{ fontSize: "11px", fontWeight: 700, color: "var(--text-muted)", marginBottom: "8px", textTransform: "uppercase" }}>
                Colas
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
                <div className="custom-input-group">
                  <input 
                    type="number" 
                    value={item.colasVol} 
                    onChange={e => handleItemChange(item.id, 'colasVol', e.target.value)} 
                    className="custom-input-field" 
                    placeholder="0.00"
                  />
                  <label className="custom-input-label">Volumen (L)</label>
                </div>
                <div className="custom-input-group">
                  <input 
                    type="number" 
                    step="0.1"
                    value={item.colasAlc} 
                    onChange={e => handleItemChange(item.id, 'colasAlc', e.target.value)} 
                    className="custom-input-field" 
                    placeholder="0.0"
                  />
                  <label className="custom-input-label">% Alc. Vol.</label>
                </div>
              </div>
            </div>

          </div>

          {/* ================= ALERTA Y BALANCE DE VOLUMEN DE CORTES ================= */}
          {(() => {
            const volDestilar = parseFloat(item.volADestilar || 0);
            const puntas = parseFloat(item.puntasVol || 0);
            const mezcal = parseFloat(item.mezcalVol || 0);
            const colas = parseFloat(item.colasVol || 0);
            const sumaCortes = puntas + mezcal + colas;
            const excede = volDestilar > 0 && sumaCortes > volDestilar;

            if (volDestilar <= 0 && sumaCortes <= 0) return null;

            return (
              <div style={{ 
                marginTop: "12px", 
                padding: "8px 12px", 
                borderRadius: "6px", 
                display: "flex", 
                alignItems: "center", 
                justifyContent: "space-between",
                background: excede ? "rgba(231, 76, 60, 0.15)" : "rgba(46, 204, 113, 0.08)", 
                border: excede ? "1px solid rgba(231, 76, 60, 0.4)" : "1px solid rgba(46, 204, 113, 0.3)",
                fontSize: "12px",
                animation: "fadeIn 0.2s ease"
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  {excede ? (
                    <ReportProblemIcon style={{ color: "#e74c3c", fontSize: 18 }} />
                  ) : (
                    <CheckCircleIcon style={{ color: "#2ecc71", fontSize: 18 }} />
                  )}
                  <span style={{ fontWeight: 600, color: excede ? "#e74c3c" : "#2ecc71" }}>
                    {excede 
                      ? `¡Error! La suma de cortes (${sumaCortes} L) excede el volumen a destilar (${volDestilar} L). Excedente: ${(sumaCortes - volDestilar).toFixed(1)} L`
                      : `Suma total de cortes: ${sumaCortes} L de ${volDestilar} L a destilar`
                    }
                  </span>
                </div>

                {!excede && volDestilar > 0 && (
                  <span style={{ fontSize: "11px", color: "var(--text-muted)", fontWeight: 700 }}>
                    Rendimiento: {((sumaCortes / volDestilar) * 100).toFixed(1)}%
                  </span>
                )}
              </div>
            );
          })()}

        </div>
      ))}

      {/* BOTÓN AGREGAR REGISTRO ABAJO DE LOS N REGISTROS */}
      <div style={{ display: "flex", justifyContent: "center", marginBottom: "24px" }}>
        <button 
          type="button" 
          onClick={handleAddItem}
          className="btn-add-new"
          style={{ 
            background: "rgba(46, 204, 113, 0.12)", 
            border: "1px dashed rgba(46, 204, 113, 0.5)", 
            color: "#2ecc71", 
            padding: "12px 24px", 
            fontSize: "14px", 
            fontWeight: 700,
            width: "100%", 
            justifyContent: "center",
            borderRadius: "8px",
            transition: "all 0.2s ease"
          }}
        >
          <AddIcon style={{ fontSize: 20 }} />
          <span>+ Agregar Otra Destilación</span>
        </button>
      </div>

      {/* OBSERVACIONES GENERALES POR SOLICITUD */}
      <section className="detail-card detail-card-accent-left-green" style={{ marginBottom: "20px", padding: "18px" }}>
        <header className="detail-card-header" style={{ marginBottom: "12px" }}>
          <div className="detail-card-icon-wrapper-green" style={{ width: "30px", height: "30px" }}>
            <SettingsIcon style={{ fontSize: 16 }} />
          </div>
          <div className="detail-card-title-wrapper">
            <h2 style={{ fontSize: "15px" }}>Observaciones Generales de la Solicitud</h2>
          </div>
        </header>

        <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
          <label style={{ fontSize: "12px", fontWeight: 600, color: "var(--text-muted)" }}>
            Comentarios o Notas sobre el proceso de destilación
          </label>
          <textarea 
            value={formData.observaciones || ""} 
            onChange={e => setFormData(prev => ({ ...prev, observaciones: e.target.value }))} 
            className="custom-input-field"
            style={{ height: "85px", resize: "vertical", padding: "12px", fontSize: "13px", lineHeight: "1.4" }}
            placeholder="Escriba notas sobre el rendimiento, temperatura de leña, control de fuego, etc..."
          />
        </div>
      </section>

      {/* BOTONES DE ACCIÓN */}
      <div className="button-row">
        <button type="button" className="btn-secondary-custom" onClick={() => handleSaveDestilacion("borrador")}>Guardar Borrador</button>
        <button type="button" className="btn-primary-copper" onClick={() => handleSaveDestilacion("revision")}>
          <CheckCircleIcon style={{ fontSize: 16 }} />
          <span>Registrar Sección IV</span>
        </button>
      </div>
    </div>
  );
};

export default DestilacionSection;
